// src/pages/SquareSelectLocation/SquareSelectLocation.js
// Post-OAuth "map a Square location to this Business" step. Greenfield.
// Entry: squareOAuthCallback redirects to /integrations/square/select-location?businessId={id}
// Backend (deployed, IAM-bound, auth enforced server-side — uid === pendingDoc.userId):
//   getSquareLocations({ businessId })                 -> { locations: [{ id, name, address, status }] }
//   provisionSquareLocation({ businessId, locationId }) -> writes Business doc, deletes pending doc

import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import {
  Container, Paper, Typography, Box, List, ListItemButton, ListItemIcon,
  ListItemText, Radio, Button, CircularProgress, Chip, Divider,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import firebase from 'firebase/app';
import 'firebase/functions';

const FUNCTIONS_REGION = 'australia-southeast1';

// Reference page (ManageVenueImages) returns users to /editBusiness/:id after a
// venue-settings action; Square connection is the same class of action, so land there.
const redirectAfterProvision = (businessId) => `/editBusiness/${businessId}`;

const call = (name) =>
  firebase.app().functions(FUNCTIONS_REGION).httpsCallable(name);

const mapError = (err) => {
  if (err && (err.code === 'not-found' || err.code === 'failed-precondition')) {
    return 'This Square connection session has expired or was already completed. Please reconnect Square from your venue settings.';
  }
  if (err && err.code === 'permission-denied') {
    return 'You’re signed in as a different account than the one that started this Square connection. Sign in with that account and try again.';
  }
  return (err && err.message) || 'Something went wrong loading your Square locations.';
};

export default function SquareSelectLocation() {
  useEffect(() => { document.title = 'Connect Square | Piatto'; }, []);

  const search = useLocation().search;
  const history = useHistory();
  const businessId = new URLSearchParams(search).get('businessId');

  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [locations, setLocations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [provisioning, setProvisioning] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!businessId) {
      setStatus('error');
      setErrorMsg('Missing businessId in the URL. Please restart the Square connection.');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await call('getSquareLocations')({ businessId });
        if (cancelled) return;
        const locs = (res.data && res.data.locations) || [];
        setLocations(locs);
        if (locs.length === 1) setSelectedId(locs[0].id); // auto-select single, still confirm
        setStatus('ready');
      } catch (err) {
        if (!cancelled) { setStatus('error'); setErrorMsg(mapError(err)); }
      }
    })();
    return () => { cancelled = true; };
  }, [businessId]);

  const handleConfirm = useCallback(async () => {
    if (!selectedId) return;
    setProvisioning(true);
    setErrorMsg('');
    try {
      await call('provisionSquareLocation')({ businessId, locationId: selectedId });
      setSuccess(true);
    } catch (err) {
      setProvisioning(false);
      setErrorMsg(mapError(err));
    }
  }, [selectedId, businessId, history]);

  if (status === 'loading') {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh" role="status" aria-live="polite">
        <CircularProgress aria-label="Loading Square locations" />
      </Box>
    );
  }

  if (status === 'error') {
    return (
      <Container component="main" maxWidth="sm" sx={{ mt: 4, mb: 8 }}>
        <Paper elevation={2} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Couldn’t connect Square</Typography>
          <Typography color="text.secondary" gutterBottom>{errorMsg}</Typography>
          {businessId && (
            <Button color="primary" sx={{ mt: 1 }} onClick={() => history.push(redirectAfterProvision(businessId))}>
              Back to venue
            </Button>
          )}
        </Paper>
      </Container>
    );
  }

  if (success) {
    const connected = locations.find((l) => l.id === selectedId);
    return (
      <Container component="main" maxWidth="sm" sx={{ mt: 4, mb: 8 }}>
        <Paper elevation={2} sx={{ p: 3 }}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <CheckCircle color="success" sx={{ fontSize: 56, mb: 1 }} aria-hidden="true" />
            <Typography variant="h5" component="h1" gutterBottom>Square connected</Typography>
            <Typography color="text.secondary">
              {connected ? `${connected.name} is` : 'Your venue is'} now set up to take accessible orders through Piatto.
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Finish setting up your venue
          </Typography>
          <Typography color="text.secondary" paragraph>
            Head to your venue settings to complete your listing. You can edit your
            business details — trading hours, service options, meals served, amenities,
            contact details, and accessibility features — add venue photos, and design
            your floor plan and seating so diners know what to expect before they arrive.
          </Typography>

          <Button
            variant="contained"
            size="large"
            fullWidth
            sx={{ mt: 1 }}
            onClick={() => history.push(redirectAfterProvision(businessId))}
          >
            Go to venue settings
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="sm" sx={{ mt: 4, mb: 8 }}>
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Choose your Square location</Typography>
        <Typography color="text.secondary" gutterBottom>
          Select which Square location to connect to this venue.
        </Typography>

        {locations.length === 0 ? (
          <Typography color="text.secondary" sx={{ mt: 2 }}>
            No Square locations were found for this account.
          </Typography>
        ) : (
          <List>
            {locations.map((loc) => {
              const inactive = loc.status && loc.status !== 'ACTIVE';
              return (
                <ListItemButton
                  key={loc.id}
                  selected={selectedId === loc.id}
                  onClick={() => setSelectedId(loc.id)}
                >
                  <ListItemIcon>
                    <Radio
                      checked={selectedId === loc.id}
                      tabIndex={-1}
                      inputProps={{ 'aria-label': `Select ${loc.name || loc.id}` }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {loc.name || loc.id}
                        {inactive && <Chip size="small" label={loc.status} />}
                      </Box>
                    }
                    secondary={loc.address || undefined}
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}

        {errorMsg && (
          <Typography color="error" sx={{ mt: 1 }}>{errorMsg}</Typography>
        )}

        <Box display="flex" justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            disabled={!selectedId || provisioning}
            onClick={handleConfirm}
          >
            {provisioning ? <CircularProgress size={22} color="inherit" /> : 'Connect this location'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}
